import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useDevices } from '../hooks/useDevices'
import { PageText } from '../components/atoms/PageText'
import { Box, Button } from '@mui/material'
import { callAxios, useAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Widget, WidgetData } from '../classes/widgets/Widget'
import { useRef, useState } from 'react'
import { getWidget } from '../classes/widgets'
import { useMessageFilter } from '../hooks/useMessageFilter'
import { isUpdateMessage } from '../types/messaging'
import { Wizard } from '../components/Wizard'

export function TestAppliancePage() {
  const { t } = useTranslation()
  const { widgetId } = useParams()
  const app = useApp()
  const [widget, setWidget] = useState<Widget>()
  const [start, setStart] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [seenMessage, setSeenMessage] = useState(false)
  const { control } = useDevices()
  const navigate = useNavigate()

  if (!start) {
    setStart(new Date().getTime())
  }

  setInterval(() => {
    const now = new Date().getTime()
    setElapsed(Math.round((now - start) / 1000))
  }, 1000)

  const onSkip = async () => {
    control.clearPendingConfig()
    navigate('/appliances')
  }

  const onAbandon = async () => {
    if (!widget) return

    // The server switches each appliance's ports off on the device as part
    // of the DELETE (ApplianceController.delete); no client-side flash here.
    for (const appl of widget.appliances) {
      const res = await callAxios<WidgetData>({
        method: 'DELETE',
        url: `/appliances/${appl.id}`,
      })
      if (!res.success) app.addError(res)
    }

    navigate('/appliances')
  }

  const onAccept = () => {
    control.clearPendingConfig()
    navigate('/appliances')
  }

  useAxios<WidgetData>({
    url: `/widgets/${widgetId}`,
    disabled: !!widget,
    onSuccess: data => {
      const widget = getWidget(data, control, app)
      widget.connectControls(control)
      setWidget(widget)
    }
  })

  const wref = useRef<Widget | null>(null)
  if (widget) {
    wref.current = widget
  }

  useMessageFilter({
    filter: s => {
      if (!wref.current) return false
      const portIds = new Set(wref.current.ports.map(p => p.id))
      return isUpdateMessage(s) && portIds.has(s.port_id) && !s.refresh
    },
    onMatch: e => {
      setSeenMessage(true)
    }
  })

  if (!widget) return <></>

  return <>
    <TopTitle title={t('Testing {name}').replace('{name}', widget ? widget?.getName() : '')}></TopTitle>
    <Box sx={{ mt: 2, textAlign: 'center', height: '3.2rem' }}>
      {!seenMessage && <PageText >{t('Verifying connection. Waiting to receive data... (elapsed time {seconds}s).').replace('{seconds}', `${elapsed}`)}</PageText>}
      {seenMessage && <PageText>{t('Receiving data')}</PageText>}
      <Box sx={{ textAlign: 'left' }}>
      {widget.getTestingInfo()}
      </Box>
    </Box>
    <Box>
      {!seenMessage &&
        <Box sx={{ display: 'flex', height: '15rem', justifyContent: 'center', alignItems: 'center' }}>
          <Wizard waiting/>
        </Box>
      }
      {seenMessage &&
        <Box>
          {widget.drawTestView({})}
        </Box>
      }
    </Box>

    <Box sx={{ mt: 4, textAlign: 'center' }}>
      {!seenMessage && <Button fullWidth onClick={onSkip} data-cy="button-Skip Test"><Trans>Skip Test</Trans></Button>}
      {seenMessage && <Button fullWidth onClick={onAccept} data-cy="button-Looking Good"><Trans>Looking Good</Trans></Button>}
    </Box>

    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <Button fullWidth onClick={onAbandon} sx={{ backgroundColor: 'error.main' }} data-cy="button-Abandon"><Trans>Abandon Appliance</Trans></Button>
    </Box>
  </>
}
